import { CdkDragDrop, moveItemInArray, transferArrayItem, CdkDropList, CdkDrag } from "@angular/cdk/drag-drop";
import { AfterViewInit, ChangeDetectorRef, Component, Input, OnInit, QueryList, ViewChildren, ChangeDetectionStrategy } from "@angular/core";
import { PartnerResolver, PartnerResolverType } from "../../partner/models/partner-resolver";
import { PartnerResolverService } from "../../partner/services/partner-resolver.service";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormGroup } from "@angular/forms";
import { distinctUntilChanged } from "rxjs/operators";
import { DataFieldGroup } from '../../shared/datafields/models';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { ToastService, ToastType } from "../../shared/toast/toast.service";
import { DeveloperPartnerFallbackResolverConfigComponent } from "./developer-partner-fallback-resolver-config.component";


@Component({
    selector: 'app-developer-partner-fallback-resolver',
    templateUrl: './developer-partner-fallback-resolver.component.html',
    styleUrls: ['./developer-partner-fallback-resolver.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [CdkDropList, CdkDrag, DeveloperPartnerFallbackResolverConfigComponent]
})
export class DeveloperPartnerFallbackResolverComponent implements AfterViewInit {
    constructor(private partnerResolverService: PartnerResolverService, private cdr: ChangeDetectorRef, private loadingPanelService: LoadingPanelService, private toastService: ToastService, private formBuilder: UntypedFormBuilder,) {
    }

    @Input() partnerResolverType: PartnerResolverType;
    @ViewChildren(DeveloperPartnerFallbackResolverConfigComponent) partnerResolverConfigComponents: QueryList<DeveloperPartnerFallbackResolverConfigComponent>;
    
    public partnerResolvers: PartnerResolver[] = [];
    formGroup: UntypedFormGroup;
    isFormValid: boolean = false;
    isEditing: boolean = false;

    async ngAfterViewInit(): Promise<void> {
        await this.refresh();
    }

    async refresh() {
        await this.loadData();
        await this.prepareForm();
    }

    async prepareForm(): Promise<void>{
        this.formGroup = this.formBuilder.group({
            partnerResolvers: this.formBuilder.array([])
        });

        setTimeout(() =>{
            this.partnerResolverConfigComponents?.toArray().forEach(d => {
                let partnerResolversArray = this.formGroup.controls.partnerResolvers as UntypedFormArray;
                let arrayLen = partnerResolversArray.length;
                partnerResolversArray.insert(arrayLen, d.formGroup);
            });
        });

        this.isFormValid = this.formGroup.valid;

        this.formGroup.statusChanges
        .pipe(distinctUntilChanged((a, b) => JSON.stringify(a) === JSON.stringify(b)))
        .subscribe(result => {
            this.isFormValid = (result === 'VALID');
            this.cdr.detectChanges();
        });
    }

    async loadData() {
        try {
            this.loadingPanelService.showLoadingPanelDirect('Loading...');
            this.partnerResolvers = await this.partnerResolverService.getPartnerResolvers(this.partnerResolverType);
        } catch(err) {
            console.error(err);
            this.toastService.showToast('Failed to load data...', ToastType.error);
        } finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    drop(event: CdkDragDrop<PartnerResolver[]>) {
        if (event.previousContainer === event.container) {
            moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
        } else {
            transferArrayItem(
                event.previousContainer.data,
                event.container.data,
                event.previousIndex,
                event.currentIndex
            );
        }
    }

    handleSortOrderChanges(enabledPartnerResolvers: PartnerResolver[]) {
        for (let i = 0; i < enabledPartnerResolvers.length; i++) {
            enabledPartnerResolvers[i].SortOrder = i;
        }
    }

    toggleEditMode() {
        this.isEditing = !this.isEditing;
    }

    async cancelEditMode() {
        await this.refresh();
        this.toggleEditMode();
    }

    async save() {
        this.toggleEditMode();
        this.handleSortOrderChanges(this.partnerResolvers);
        this.partnerResolvers.forEach(pr => pr.ConfigurationDataFieldValues = DataFieldGroup.getDataFieldValues(pr.ConfigurationDataFields, true))
        this.loadingPanelService.showLoadingPanelDirect('Saving...');
        await this.partnerResolverService.savePartnerResolvers(this.partnerResolvers);
        this.loadingPanelService.hideLoadingPanel();
        await this.refresh();
    }
}